/**
 * In-person matching (per-session assignments).
 */

import { apiClient } from '@/services/api-client';
import type { SessionStudentTutorPair } from '@/types/inperson/pair.types';
import type {
  AutoAssignmentProposal,
  ProposeDraftAutoAssignmentDto,
  SetAssignmentsDto,
} from '@/types/inperson/matching.types';

/**
 * Replace every pair on the session. Created pairs have source `manual`.
 * PUT /sessions/:sessionId/assignments
 */
export const setSessionAssignments = async (
  sessionId: string,
  dto: SetAssignmentsDto,
): Promise<SessionStudentTutorPair[]> => {
  const { data } = await apiClient.put<SessionStudentTutorPair[]>(
    `/sessions/${sessionId}/assignments`,
    dto,
  );
  return data;
};

/**
 * Auto-assign: keep existing `manual` pairs, then fill the rest (explicit / implicit
 * core pair ≥5 sessions, then balancing).
 * POST /sessions/:sessionId/assignments/auto
 */
export const runSessionAutoAssignment = async (
  sessionId: string,
  participants?: { userId: string; role: 'student' | 'tutor' | 'volunteer' }[],
): Promise<SessionStudentTutorPair[]> => {
  const { data } = await apiClient.post<SessionStudentTutorPair[]>(
    `/sessions/${sessionId}/assignments/auto`,
    { participants },
  );
  return data;
};

/**
 * Propose auto-assignment for a session that does not exist yet (create flow).
 * The client sends full context; the backend returns proposed pairs without persisting.
 * POST /sessions/assignments/auto/propose-draft
 */
export const proposeDraftAutoAssignment = async (
  dto: ProposeDraftAutoAssignmentDto,
): Promise<AutoAssignmentProposal> => {
  const { data } = await apiClient.post<AutoAssignmentProposal>(
    `/sessions/assignments/auto/propose-draft`,
    dto,
  );
  return data;
};
